/**
 * Chat-agent model registry (2026-10-05) — what the chat agent has to know
 * about a Claude model before it builds a request or prices a response.
 *
 * WHY THIS EXISTS. The chat request used to be the same for every model: one
 * output cap, nothing said about thinking or effort. That was right while
 * every selectable model answered without thinking. It stopped being right the
 * day a studio picked Claude Sonnet 5, which thinks before it answers unless
 * told otherwise — and thinking is billed as output and counts toward
 * `max_tokens`. Replaying 35 production first steps on that model, one needed
 * 2,641 output tokens against a 2,048 cap; cut off mid-thought it has no text
 * and no tool call, and the member is told "Sorry, I'm having trouble". The
 * same hand-kept price table also priced the model by family name, 1.5x its
 * list price, which the daily spend cap then added up.
 *
 * Single source of truth for:
 *
 *   - the request settings `runTranslation` derives per model
 *     (`packages/chat-translator/src/translator.ts`);
 *   - `estimateCostUsd` (`packages/chat-translator/src/anthropic-client.ts`);
 *   - the L2 validator's `chat_translator.effort_*` values (`l2-yaml.ts`);
 *   - the `/config/:slug` "Model per agent" selectors, which render
 *     {@link CHAT_MODEL_OPTIONS} and {@link CHAT_EFFORT_LEVELS} from here.
 *
 * Pure consts + pure functions, no deps — safe for the dashboard's browser
 * bundle (aliased in `apps/dashboard/web/vite.config.ts`).
 *
 * KEEPING IT TRUE. `acceptsEffort` is read off Anthropic's Models API
 * (`GET /v1/models/<id>` → `capabilities.effort.supported`; the lookup is free
 * and needs no model call). `thinksByDefault` and the rates come from
 * Anthropic's model and pricing pages. `acceptsForcedToolChoice` comes from the
 * thinking page, "Response prefill and forced tool use": forced tool use "works
 * with adaptive thinking. The exceptions are Claude Opus 5.5, Claude Sonnet
 * 5.5, Claude Fable 5.1, and Claude Mythos 5.1, which reject forced tool use
 * on every request with a 400 error" (read 2026-10-14). The Models API does
 * not report it. Offering a model in `/config` means
 * adding its row here FIRST — `tests/chat-models.test.ts` fails on an option
 * that resolves to a family fallback.
 */

/**
 * The effort levels a studio may set per chat tier. Deliberately the three
 * every effort-capable model accepts: `xhigh` and `max` are rejected by some
 * (Sonnet 4.6 has no `xhigh`, Opus 4.5 neither), and both are for long
 * autonomous work — a chat turn has a 60-second budget and a person waiting.
 */
export const CHAT_EFFORT_LEVELS = ["low", "medium", "high"] as const;

export type ChatEffort = (typeof CHAT_EFFORT_LEVELS)[number];

export function isChatEffort(value: unknown): value is ChatEffort {
  return (
    typeof value === "string" &&
    (CHAT_EFFORT_LEVELS as ReadonlyArray<string>).includes(value)
  );
}

/** Base rates in USD per 1M tokens. */
export interface ChatModelRates {
  readonly input: number;
  readonly output: number;
  /**
   * Prompt-cache READS. Stated per model, not as a multiplier of `input`: it
   * is 0.1x on most models but 0.05x on Opus 5.5 and Sonnet 5.5 and 0.025x
   * on Fable 5.1.
   */
  readonly cacheRead: number;
}

/**
 * 2026-10-10 — a second set of rates for a model whose price depends on how
 * long the prompt is. Claude Haiku 5.5 is the first: "a request whose prompt
 * is over 100,000 tokens pays higher prices. A request's prompt length counts
 * all of its input tokens, including cache reads and cache writes. Each
 * request is priced on its own" (Anthropic's pricing page, read 2026-10-10).
 * So the step is per REQUEST and prices the WHOLE request — output included —
 * never only the tokens past the threshold.
 */
export interface ChatModelLongPromptRates {
  /** A prompt of MORE than this many tokens pays `rates`; exactly this many does not. */
  readonly overPromptTokens: number;
  readonly rates: ChatModelRates;
}

export interface ChatModelProfile {
  /**
   * Substring of the API model id this row covers, so a dated id
   * (`claude-haiku-4-5-20251001`) or a platform-prefixed one
   * (`anthropic.claude-sonnet-5-5`) resolves the same.
   */
  readonly match: string;
  readonly rates: ChatModelRates;
  /** ABSENT ⇒ one price whatever the prompt length (every model but Haiku 5.5). */
  readonly longPrompt?: ChatModelLongPromptRates;
  /**
   * The model accepts `output_config.effort`. Sending it to one that does not
   * is a 400 on every turn — and the spend cap's fallback model is one of
   * those, so a configured effort MUST be dropped rather than forwarded.
   */
  readonly acceptsEffort: boolean;
  /**
   * A request that says nothing about `thinking` runs with thinking ON. Those
   * tokens count toward `max_tokens`, so the cap has to leave room for them.
   */
  readonly thinksByDefault: boolean;
  /**
   * 2026-10-14 (`chat-final-reply-and-step-budget`) — the model accepts a
   * FORCED tool call (`tool_choice` of type `tool`), so the chat agent's last
   * model call can be made to answer with `send_reply` instead of reading
   * again. Sending one to a model that refuses it is a 400 on that call, so
   * not knowing means false: the last call then goes out as every other does.
   */
  readonly acceptsForcedToolChoice: boolean;
}

/**
 * Most specific first: {@link resolveChatModelProfile} takes the first row
 * whose `match` occurs in the id, so `claude-opus-5-5` must sit above
 * `claude-opus-5`, and every exact row above its family fallback.
 */
export const CHAT_MODEL_PROFILES: ReadonlyArray<ChatModelProfile> = [
  {
    match: "claude-fable-5-1",
    rates: { input: 10, output: 50, cacheRead: 0.25 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-fable-5",
    rates: { input: 10, output: 50, cacheRead: 1 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-opus-5-5",
    rates: { input: 4, output: 20, cacheRead: 0.2 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-opus-5",
    rates: { input: 5, output: 25, cacheRead: 0.5 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-opus-4-8",
    rates: { input: 5, output: 25, cacheRead: 0.5 },
    acceptsEffort: true,
    thinksByDefault: false,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-opus-4-7",
    rates: { input: 5, output: 25, cacheRead: 0.5 },
    acceptsEffort: true,
    thinksByDefault: false,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-opus-4-6",
    rates: { input: 5, output: 25, cacheRead: 0.5 },
    acceptsEffort: true,
    thinksByDefault: false,
    acceptsForcedToolChoice: true,
  },
  // Sonnet 5.5 above Sonnet 5: same input and output price, but a cache read
  // is 0.05x input here against 0.1x there. Until 2026-10-10 one row covered
  // both, so every Sonnet 5.5 read — most of a staff conversation's tokens —
  // was estimated at twice its list price.
  {
    match: "claude-sonnet-5-5",
    rates: { input: 2, output: 10, cacheRead: 0.1 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-sonnet-5",
    rates: { input: 2, output: 10, cacheRead: 0.2 },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-sonnet-4-6",
    rates: { input: 3, output: 15, cacheRead: 0.3 },
    acceptsEffort: true,
    thinksByDefault: false,
    acceptsForcedToolChoice: true,
  },
  // Haiku 5.5 (2026-10-07): a tenth of Haiku 4.5's price up to 100,000 prompt
  // tokens and five times that above. It thinks by default (adaptive, effort
  // `medium`) and accepts effort, unlike Haiku 4.5.
  {
    match: "claude-haiku-5-5",
    rates: { input: 0.1, output: 0.5, cacheRead: 0.01 },
    longPrompt: {
      overPromptTokens: 100_000,
      rates: { input: 0.5, output: 2.5, cacheRead: 0.05 },
    },
    acceptsEffort: true,
    thinksByDefault: true,
    acceptsForcedToolChoice: true,
  },
  {
    match: "claude-haiku-4-5",
    rates: { input: 1, output: 5, cacheRead: 0.1 },
    acceptsEffort: false,
    thinksByDefault: false,
    acceptsForcedToolChoice: true,
  },
  // Family fallbacks — an id with no exact row above. The rates are the
  // family's usual ones. The flags take the SAFE side of not knowing:
  // effort is not sent (it cannot 400 a turn it was never on), the cap
  // leaves room for thinking (a model that does not think never uses it), and
  // the last call is not forced (Opus 5.5, Sonnet 5.5 and Fable 5.1 refuse
  // it, so a newer id of the family may too).
  {
    match: "claude-fable",
    rates: { input: 10, output: 50, cacheRead: 1 },
    acceptsEffort: false,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-opus",
    rates: { input: 5, output: 25, cacheRead: 0.5 },
    acceptsEffort: false,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-sonnet",
    rates: { input: 3, output: 15, cacheRead: 0.3 },
    acceptsEffort: false,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
  {
    match: "claude-haiku",
    rates: { input: 1, output: 5, cacheRead: 0.1 },
    acceptsEffort: false,
    thinksByDefault: true,
    acceptsForcedToolChoice: false,
  },
];

/**
 * An id that matches no row at all (a typo that still passed the `claude-…`
 * format gate, or a family that postdates this file): Sonnet rates — the
 * client default the estimate has always fallen back to — and the same safe
 * side on every flag as the family rows.
 */
export const UNKNOWN_CHAT_MODEL_PROFILE: ChatModelProfile = {
  match: "",
  rates: { input: 3, output: 15, cacheRead: 0.3 },
  acceptsEffort: false,
  thinksByDefault: true,
  acceptsForcedToolChoice: false,
};

export function resolveChatModelProfile(model: string): ChatModelProfile {
  const id = model.toLowerCase();
  for (const profile of CHAT_MODEL_PROFILES) {
    if (id.includes(profile.match)) return profile;
  }
  return UNKNOWN_CHAT_MODEL_PROFILE;
}

/**
 * The rates ONE request is billed at: the row's long-prompt rates when it has
 * them and this request's prompt is over the threshold, the row's ordinary
 * rates otherwise. `promptTokens` is everything the request sent as input —
 * uncached input, cache reads and cache writes together.
 */
export function chatModelRatesForPrompt(
  profile: ChatModelProfile,
  promptTokens: number,
): ChatModelRates {
  const long = profile.longPrompt;
  return long !== undefined && promptTokens > long.overPromptTokens
    ? long.rates
    : profile.rates;
}

/**
 * The models `/config/:slug` offers per chat tier. `id` is the Anthropic
 * model id written to `chat_translator.model_member` / `model_staff`.
 * Fable-tier models are left out on purpose: far pricier, with nothing to
 * show for it on a booking agent.
 */
export const CHAT_MODEL_OPTIONS: ReadonlyArray<{
  readonly id: string;
  readonly label: string;
}> = [
  { id: "claude-haiku-5-5", label: "Haiku 5.5 — cheapest, fast" },
  { id: "claude-haiku-4-5", label: "Haiku 4.5 — older small model" },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6 — balanced (default)" },
  { id: "claude-sonnet-5", label: "Sonnet 5 — near-Opus quality, agentic" },
  { id: "claude-sonnet-5-5", label: "Sonnet 5.5 — newest Sonnet, Sonnet 5's price" },
  { id: "claude-opus-4-8", label: "Opus 4.8 — most capable, slowest" },
];
